import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useTheme,
  Box,
} from "@mui/material";
import CustomTypography from "../common/form/CustomTypography";
import AppButton from "../common/buttons/AppButton";
import AppDropDown from "../common/filter/AppDropDown";
import { FilterValue } from "../../../models/Filters/FilterValue";
import FormTextField from "../common/form/FormTextField";

const CANCEL_REASONS = [
  "Duplicate shipment",
  "On customer request",
  "Wrong address",
  "Out of stock",
  "Other",
];

const OTHER_VALUE = "Other";

interface CancelShipmentPopupProps {
  open: boolean;
  shipmentId: number | null;
  onConfirm: (shipmentId: number, reason: string) => void;
  onCancel: () => void;
}

const CancelShipmentPopup: React.FC<CancelShipmentPopupProps> = ({
  open,
  shipmentId,
  onConfirm,
  onCancel,
}) => {
  const theme = useTheme();
  const [selectedReason, setSelectedReason] = useState<FilterValue | null>(
    null,
  );
  const [customReason, setCustomReason] = useState<string>("");

  useEffect(() => {
    if (!open) {
      setSelectedReason(null);
      setCustomReason("");
    }
  }, [open]);

  const handleConfirm = () => {
    if (!shipmentId) return;
    const reason =
      selectedReason?.value === OTHER_VALUE
        ? customReason.trim() || "Other"
        : (selectedReason?.value ?? "");
    if (!reason.trim()) return;
    onConfirm(shipmentId, reason.trim());
    onCancel();
  };

  const isValid = selectedReason
    ? selectedReason?.value !== OTHER_VALUE || customReason.trim().length > 0
    : false;

  return (
    <Dialog
      open={open}
      onClose={onCancel}
      maxWidth="sm"
      fullWidth
      slotProps={{
        transition: { timeout: 200 },
        paper: {
          sx: {
            borderRadius: 2,
            boxShadow: "0px 24px 48px rgba(0, 0, 0, 0.12)",
          },
        },
      }}
    >
      <DialogTitle>
        <CustomTypography variant="heading6" fontWeight={600}>
          Cancel shipment
        </CustomTypography>
      </DialogTitle>
      <DialogContent>
        <CustomTypography
          variant="body2"
          color={theme.palette.text.secondary}
          style={{ marginBottom: 30 }}
        >
          Please provide a reason for cancelling this shipment.
        </CustomTypography>
        <AppDropDown
          isMultiSelect={false}
          title="Reason"
          value={selectedReason?.id ?? ""}
          fontSize={"12px"}
          list={CANCEL_REASONS.map((reason) => ({
            id: reason,
            value: reason,
          }))}
          onSingleSelectionChange={(selectedId) =>
            setSelectedReason({
              id: selectedId,
              value: selectedId,
            })
          }
        />
        {selectedReason?.value === OTHER_VALUE && (
          <FormTextField
            id="customReason"
            label="Reason"
            value={customReason}
            onChange={(id, value) => setCustomReason(value ?? "")}
            multiline
            rows={3}
            fontSize="12px"
            sx={{ mt: 2 }}
          />
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, pt: 0 }}>
        <Box sx={{ display: "flex", gap: 1.5 }}>
          <AppButton variant="grayscale" label="Cancel" onClick={onCancel} />
          <AppButton
            variant="primary"
            label="Confirm cancel"
            onClick={handleConfirm}
            disabled={!isValid}
          />
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default CancelShipmentPopup;
